import React, { useEffect, useState, useCallback } from "react";
import { Button, Table, Form, Spinner } from "react-bootstrap";

import ProctoringAdminService from "../../_services/ProctoringAdminService";
import SessionDetailModal from "./components/SessionDetailModal";

interface Session {
  TestId: number;
  testName: string;
  EnquiryId: number;
  studentName: string;
  firstActivity: string;
  lastActivity: string;
  totalViolations: number;
  recordingChunks: number;
  reviewStatus: string | null;
  lastReviewedAt: string | null;
}

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "unreviewed", label: "Unreviewed" },
  { value: "flagged", label: "Flagged" },
  { value: "cleared", label: "Cleared" },
  { value: "needs_review", label: "Needs Review" },
  { value: "blocked", label: "Blocked" },
];

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  flagged: { bg: "#fef2f2", color: "var(--status-danger)" },
  cleared: { bg: "#f0fdf4", color: "var(--status-success)" },
  needs_review: { bg: "#fffbeb", color: "var(--status-warning)" },
  blocked: { bg: "#f5f3ff", color: "var(--brand-accent)" },
};

const ProctoringReview: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  // Filters
  const [filterTestId, setFilterTestId] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterStudent, setFilterStudent] = useState("");

  // Detail modal
  const [detailTestId, setDetailTestId] = useState<number | null>(null);
  const [detailEnquiryId, setDetailEnquiryId] = useState<number | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (filterTestId) params.testId = filterTestId;
      if (filterDateFrom) params.dateFrom = filterDateFrom;
      if (filterDateTo) params.dateTo = filterDateTo;
      if (filterStatus) params.reviewStatus = filterStatus;
      if (filterStudent) params.enquiryId = filterStudent;

      const res = await ProctoringAdminService.getSessions(params);
      const d = res.data.data;
      setSessions(d.sessions || []);
      setTotal(d.total || 0);
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setLoading(false);
    }
  }, [page, filterTestId, filterDateFrom, filterDateTo, filterStatus, filterStudent]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSearch = () => {
    setPage(0);
    fetchSessions();
  };

  const handleClearFilters = () => {
    setFilterTestId("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterStatus("");
    setFilterStudent("");
    setPage(0);
  };

  const handleViewDetail = (s: Session) => {
    setDetailTestId(s.TestId);
    setDetailEnquiryId(s.EnquiryId);
    setShowDetail(true);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Summary counts
  const flaggedCount = sessions.filter((s) => s.reviewStatus === "flagged").length;
  const clearedCount = sessions.filter((s) => s.reviewStatus === "cleared").length;
  const unreviewedCount = sessions.filter((s) => !s.reviewStatus).length;

  return (
    <div style={{ padding: 20 }}>
      <h5 style={{ marginBottom: 16 }}>Proctoring Audit Review</h5>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: 16,
          padding: 12,
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          background: "var(--surface-page)",
        }}
      >
        <Form.Group style={{ minWidth: 120 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Test ID</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            placeholder="Test ID"
            value={filterTestId}
            onChange={(e) => setFilterTestId(e.target.value)}
          />
        </Form.Group>
        <Form.Group style={{ minWidth: 140 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Date From</Form.Label>
          <Form.Control
            size="sm"
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
          />
        </Form.Group>
        <Form.Group style={{ minWidth: 140 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Date To</Form.Label>
          <Form.Control
            size="sm"
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
          />
        </Form.Group>
        <Form.Group style={{ minWidth: 150 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Status</Form.Label>
          <Form.Select
            size="sm"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group style={{ minWidth: 120 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Enquiry ID</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            placeholder="Enquiry ID"
            value={filterStudent}
            onChange={(e) => setFilterStudent(e.target.value)}
          />
        </Form.Group>
        <Button size="sm" variant="primary" onClick={handleSearch}>
          Search
        </Button>
        <Button size="sm" variant="secondary" onClick={handleClearFilters}>
          Clear
        </Button>
      </div>

      {/* Summary */}
      <div style={{ fontSize: 13, marginBottom: 12, color: "var(--text-body)" }}>
        {total} sessions
        {flaggedCount > 0 && (
          <span style={{ marginLeft: 12, color: "var(--status-danger)" }}>
            {flaggedCount} flagged
          </span>
        )}
        {clearedCount > 0 && (
          <span style={{ marginLeft: 12, color: "var(--status-success)" }}>
            {clearedCount} cleared
          </span>
        )}
        {unreviewedCount > 0 && (
          <span style={{ marginLeft: 12, color: "var(--text-faint)" }}>
            {unreviewedCount} unreviewed
          </span>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner animation="border" size="sm" /> Loading sessions...
        </div>
      ) : sessions.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "var(--text-muted)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
          }}
        >
          No proctored sessions found matching filters.
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <Table striped bordered hover size="sm" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Test</th>
                  <th>Date</th>
                  <th style={{ textAlign: "center" }}>Violations</th>
                  <th style={{ textAlign: "center" }}>Recordings</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const statusStyle = s.reviewStatus
                    ? STATUS_STYLES[s.reviewStatus]
                    : null;
                  return (
                    <tr key={`${s.TestId}-${s.EnquiryId}`}>
                      <td>{s.studentName}</td>
                      <td>{s.testName}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDate(s.lastActivity)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "1px 8px",
                            borderRadius: 4,
                            background:
                              Number(s.totalViolations) >= 4
                                ? "#dc2626"
                                : Number(s.totalViolations) >= 1
                                  ? "#d97706"
                                  : "#16a34a",
                            color: "var(--text-on-brand)",
                            fontSize: 12,
                          }}
                        >
                          {s.totalViolations}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {s.recordingChunks} chunks
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {s.reviewStatus ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "1px 8px",
                              borderRadius: 4,
                              background: statusStyle?.bg || "#f0f0f0",
                              color: statusStyle?.color || "#333",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {s.reviewStatus}
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-faint)", fontSize: 11 }}>
                            Unreviewed
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          size="sm"
                          variant="primary"
                          style={{ fontSize: 11, padding: "2px 8px" }}
                          onClick={() => handleViewDetail(s)}
                        >
                          Review
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 12,
              }}
            >
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Showing {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Session Detail Modal */}
      <SessionDetailModal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        testId={detailTestId}
        enquiryId={detailEnquiryId}
        onReviewSubmitted={fetchSessions}
        onStudentBlocked={fetchSessions}
      />
    </div>
  );
};

export default ProctoringReview;
